import { useSnackbar } from 'notistack'
import { t, Trans } from '@lingui/macro'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button, Stack, Typography } from '@mui/material'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import {
  useGetExpertiseMeetingQuery,
  useGetExpertiseMeetingRequestAccessQuery,
  useSendExpertiseMeetingRequestAccessMutation,
} from '@/core/services/expertise-meeting'
import { getApiErrorMessage } from '@/utils/errors'
import { ExpertiseMeetingRequestAccessStatusEnum } from '@/core/models/expertise-meeting.model'

export function AskForJoin({ expertiseMeetingId }: { expertiseMeetingId?: string }) {
  const { data: requestAccessSent } = useGetExpertiseMeetingRequestAccessQuery(expertiseMeetingId ?? skipToken)
  useGetExpertiseMeetingQuery(expertiseMeetingId ?? skipToken, { skip: requestAccessSent !== ExpertiseMeetingRequestAccessStatusEnum.ACCEPTED })

  const showRequestAccessButton =
    requestAccessSent !== ExpertiseMeetingRequestAccessStatusEnum.SENT && requestAccessSent !== ExpertiseMeetingRequestAccessStatusEnum.ACCEPTED

  return (
    <Stack flex={1} display="flex" alignItems="center" justifyContent="center" spacing={2}>
      <RequestAccessStatus requestAccessSent={requestAccessSent} />
      {showRequestAccessButton && <SendRequestAccessButton expertiseMeetingId={expertiseMeetingId} />}
    </Stack>
  )
}

function RequestAccessStatus({ requestAccessSent }: { requestAccessSent?: ExpertiseMeetingRequestAccessStatusEnum }) {
  if (requestAccessSent === ExpertiseMeetingRequestAccessStatusEnum.SENT) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
        <Trans>Your request for join has been sent. Please wait for acceptance.</Trans>
      </Stack>
    )
  }

  if (requestAccessSent === ExpertiseMeetingRequestAccessStatusEnum.DECLINED) {
    return (
      <Typography variant="body1">
        <Trans>Your request for join has been declined. Please try again.</Trans>
      </Typography>
    )
  }

  return null
}

function SendRequestAccessButton({ expertiseMeetingId }: { expertiseMeetingId?: string }) {
  const { enqueueSnackbar } = useSnackbar()
  const [sendRequestAccess] = useSendExpertiseMeetingRequestAccessMutation()

  if (!expertiseMeetingId) return null

  return (
    <Button
      variant="contained"
      size="large"
      onClick={async () => {
        try {
          await sendRequestAccess(expertiseMeetingId).unwrap()
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t`An error occurred while asking for join. Please try again!`), { variant: 'error' })
        }
      }}
    >
      <Trans>Ask for join</Trans>
    </Button>
  )
}
